import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Trash2, Plus, Ban, Upload, FileSpreadsheet, Send } from "lucide-react";
import { api, API } from "@/lib/api";
import { Section, Chip, StatusBadge } from "@/components/ui-brand";
import { PanelHeader, Tabs, Field, Card } from "@/components/panel";
import { InformeFactoresEditor } from "@/components/admin/InformeFactoresEditor";
import { ClaimLinks } from "@/components/admin/ClaimLinks";
import { useAuth } from "@/context/AuthContext";
import { useFeatures } from "@/context/FeaturesContext";
import { UsuariosInternos } from "@/components/admin/UsuariosInternos";
import { MetricasDesgloses } from "@/components/admin/MetricasDesgloses";

export default function AdminPanel() {
  const { user } = useAuth();
  const { comunidad } = useFeatures();
  const [tab, setTab] = useState("metricas");
  const [metrics, setMetrics] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [egresados, setEgresados] = useState([]);
  const [catalogs, setCatalogs] = useState({ skill_hard: [], skill_soft: [], formacion: [] });
  const [audit, setAudit] = useState([]);
  const [selEgr, setSelEgr] = useState(null);
  const [newCat, setNewCat] = useState({ tipo: "skill_hard", nombre: "" });
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);

  const [feed, setFeed] = useState([]);
  const [newFeed, setNewFeed] = useState({ tipo: "noticia", titulo: "", detalle: "" });
  const loadFeed = () => api.get("/public/feed?limit=100").then((r) => setFeed(r.data.items)).catch(() => {});
  const crearFeed = async () => {
    if (!newFeed.titulo.trim()) { toast.error("El título es obligatorio"); return; }
    try {
      await api.post("/admin/feed", { tipo: newFeed.tipo, titulo: newFeed.titulo.trim(), detalle: newFeed.detalle.trim() });
      toast.success("Publicado en la comunidad");
      setNewFeed({ tipo: "noticia", titulo: "", detalle: "" }); loadFeed();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al publicar"); }
  };
  const delFeed = async (fid) => {
    if (!window.confirm("¿Eliminar esta publicación de la comunidad?")) return;
    try { await api.delete(`/admin/feed/${fid}`); toast.success("Publicación eliminada"); loadFeed(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error al eliminar"); }
  };

  const downloadTemplate = async () => {
    const r = await api.get("/admin/egresados/import-template", { responseType: "blob" });
    const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = "plantilla_fortalezas.csv"; a.click(); URL.revokeObjectURL(url);
  };
  const importCsv = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    setImporting(true); setImportResult(null);
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post("/admin/egresados/import-fortalezas", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(r.data); toast.success(`${r.data.actualizados} egresado(s) actualizado(s)`); loadEgresados();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al importar el CSV"); }
    finally { setImporting(false); e.target.value = ""; }
  };

  const loadEmpresas = () => api.get("/admin/empresas").then((r) => setEmpresas(r.data.items));
  const loadEgresados = () => api.get("/admin/egresados").then((r) => setEgresados(r.data.items));
  const loadCatalogs = () => api.get("/catalogs").then((r) => setCatalogs(r.data));

  const [baseResult, setBaseResult] = useState(null);
  const [claimLinks, setClaimLinks] = useState([]);
  const [baseImporting, setBaseImporting] = useState(false);
  const [pendientes, setPendientes] = useState([]);
  const loadPendientes = () => api.get("/admin/egresados/pendientes?limit=100").then((r) => setPendientes(r.data.items)).catch(() => {});
  const downloadBaseTemplate = async () => {
    const r = await api.get("/admin/egresados/import-base-template", { responseType: "blob" });
    const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = "plantilla_egresados.csv"; a.click(); URL.revokeObjectURL(url);
  };
  const importBase = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    setBaseImporting(true); setBaseResult(null); setClaimLinks([]);
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post("/admin/egresados/import-base", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBaseResult(r.data); toast.success(`${r.data.creados} cuenta(s) creada(s)`); loadEgresados(); loadPendientes();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al importar la base"); }
    finally { setBaseImporting(false); e.target.value = ""; }
  };
  const resendClaim = async (uid) => {
    try {
      const r = await api.post(`/admin/egresados/${uid}/reenviar-activacion`);
      if (r.data.ok) { setClaimLinks([]); toast.success(`Enlace reenviado a ${r.data.email}`); }
      else { setClaimLinks([{ email: r.data.email, link: r.data.link }]); toast.error(`No se pudo enviar el email a ${r.data.email}. Copiá el enlace y pasalo a mano.`); }
    }
    catch (err) { toast.error(err.response?.data?.detail || "Error al reenviar"); }
  };

  useEffect(() => {
    api.get("/admin/metrics").then((r) => setMetrics(r.data)).catch(() => toast.error("No se pudieron cargar las métricas"));
    loadEmpresas(); loadEgresados(); loadCatalogs(); loadPendientes(); loadFeed();
    api.get("/admin/audit").then((r) => setAudit(r.data.items));
  }, []);

  const setLead = async (id, estado_lead) => { await api.put(`/admin/empresas/${id}/lead`, { estado_lead }); loadEmpresas(); };
  const suspender = async (id) => { if (!window.confirm("¿Suspender esta empresa y cerrar sus búsquedas?")) return; await api.put(`/admin/empresas/${id}/suspender`); toast.success("Empresa suspendida"); loadEmpresas(); };
  const exportCsv = async () => {
    const r = await api.get("/admin/empresas/export", { responseType: "blob" });
    const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = "empresas_leads.csv"; a.click(); URL.revokeObjectURL(url);
    toast.success("CSV exportado");
  };
  const openInforme = (egr) => setSelEgr(egr);
  const addCat = async () => { if (!newCat.nombre.trim()) return; await api.post("/admin/catalog", newCat); toast.success("Agregado al catálogo"); setNewCat({ ...newCat, nombre: "" }); loadCatalogs(); };
  const delCat = async (tipo, nombre) => { await api.delete(`/admin/catalog?tipo=${tipo}&nombre=${encodeURIComponent(nombre)}`); loadCatalogs(); };
  const toggleDestacado = async (e) => {
    try { await api.put(`/admin/egresados/${e.id}/destacado`, { destacado: !e.destacado }); toast.success(!e.destacado ? "Marcado como destacado" : "Quitado de destacados"); loadEgresados(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error al actualizar destacado"); }
  };

  const tabs = [
    { key: "metricas", label: "Métricas" },
    { key: "empresas", label: "Empresas / Leads" },
    { key: "egresados", label: "Fortalezas Egresados/as" },
    { key: "catalogos", label: "Catálogos" },
    ...(comunidad ? [{ key: "comunidad", label: "Comunidad" }] : []),
    { key: "auditoria", label: "Auditoría" },
    ...(user?.rol === "super_admin" ? [{ key: "usuarios", label: "Usuarios internos" }] : []),
  ];

  const M = ({ v, l, testid }) => <Card className="text-center" ><div className="font-heading font-extrabold text-3xl text-[var(--brand-accent)]" data-testid={testid}>{v == null ? "—" : Number(v).toLocaleString("es-AR")}</div><div className="text-xs text-[var(--brand-text-muted)] mt-1">{l}</div></Card>;

  return (
    <div>
      <PanelHeader title="Panel de administración" subtitle="Córdoba Crea Futuro Talento"
        right={<button onClick={exportCsv} className="btn-accent flex items-center gap-2" data-testid="header-export-csv"><Download size={16} /> Exportar leads CSV</button>} />
      <Tabs tabs={tabs} active={tab} onChange={setTab} />
      <Section className="py-8">
        {tab === "metricas" && metrics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <M v={metrics.egresados} l="Egresados/as" testid="m-egresados" />
            <M v={metrics.empresas} l="Empresas" testid="m-empresas" />
            <M v={metrics.busquedas_abiertas} l="Búsquedas abiertas" testid="m-busquedas" />
            <M v={metrics.busquedas_total} l="Búsquedas totales" testid="m-busquedas-total" />
            <M v={metrics.postulaciones} l="Postulaciones" testid="m-postulaciones" />
            <M v={metrics.postulaciones_en_curso} l="Postulaciones en curso" testid="m-postulaciones-en-curso" />
            <M v={metrics.postulaciones_no_avanzadas} l="Postulaciones que no avanzaron" testid="m-postulaciones-no-avanzadas" />
            <M v={metrics.emprendimientos} l="Emprendimientos" testid="m-emprendimientos" />
          </div>
        )}
        {tab === "metricas" && metrics && <MetricasDesgloses m={metrics} />}

        {tab === "empresas" && (
          <div className="overflow-x-auto card-brand">
            <table className="w-full text-sm">
              <thead className="bg-[var(--brand-surface)] text-left"><tr>
                {["Razón social", "Contacto", "Email", "Teléfono", "Marca", "Lead", "Acciones"].map((h) => <th key={h} className="px-4 py-3 font-heading text-[var(--brand-primary)]">{h}</th>)}
              </tr></thead>
              <tbody>
                {empresas.map((e) => (
                  <tr key={e.id} className="border-t border-slate-100" data-testid={`admin-empresa-${e.id}`}>
                    <td className="px-4 py-3 font-semibold text-[var(--brand-primary)]">{e.razon_social}</td>
                    <td className="px-4 py-3">{e.contacto_nombre}</td>
                    <td className="px-4 py-3 text-[var(--brand-text-muted)]">{e.contacto_email}</td>
                    <td className="px-4 py-3 text-[var(--brand-text-muted)]">{e.contacto_telefono}</td>
                    <td className="px-4 py-3">{e.marca}</td>
                    <td className="px-4 py-3">
                      <select value={e.estado_lead} onChange={(ev) => setLead(e.id, ev.target.value)} data-testid={`lead-${e.id}`} className="rounded-full border-2 border-[var(--brand-border)] px-2 py-1 text-xs">
                        {["nuevo", "contactado", "cliente", "descartado"].map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3"><button onClick={() => suspender(e.id)} className="text-red-400 flex items-center gap-1 text-xs" data-testid={`suspender-${e.id}`}><Ban size={14} /> Suspender</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "egresados" && !selEgr && (
          <div>
            <Card className="mb-6" data-testid="base-import-card">
              <div className="flex items-center gap-2 mb-2"><Upload size={18} className="text-[var(--brand-accent)]" /><h3 className="font-heading font-bold text-[var(--brand-primary)]">Carga masiva de egresados (base)</h3></div>
              <p className="text-sm text-[var(--brand-text-muted)] mb-4">Importá la base de egresados/as. Campos mínimos: <b>nombre, mail, teléfono, localidad, formación, carga horaria</b>. Cada cuenta se crea como <b>pendiente de reclamar</b> (sin contraseña, oculta a empresas) y recibe un email con un enlace de activación que vence en 7 días.</p>
              <div className="flex flex-wrap gap-3">
                <button onClick={downloadBaseTemplate} className="btn-outline text-sm inline-flex items-center gap-2" data-testid="base-template-btn"><Download size={15} /> Descargar plantilla</button>
                <label className={`btn-accent text-sm inline-flex items-center gap-2 cursor-pointer ${baseImporting ? "opacity-60 pointer-events-none" : ""}`} data-testid="base-import-label">
                  <Upload size={15} /> {baseImporting ? "Importando…" : "Importar base"}
                  <input type="file" accept=".csv" hidden onChange={importBase} data-testid="base-import-input" />
                </label>
              </div>
              {baseResult && (
                <div className="mt-4 rounded-xl bg-[var(--brand-surface)] p-4 text-sm" data-testid="base-import-result">
                  <p className="text-[var(--brand-primary)]"><b>{baseResult.creados}</b> creados · <b>{baseResult.emails_enviados}</b> emails enviados{baseResult.existentes?.length ? ` · ${baseResult.existentes.length} ya existían` : ""}</p>
                  {baseResult.email_fallidos?.length > 0 && <p className="text-[var(--status-descartada)] mt-1">Emails no enviados ({baseResult.email_fallidos.length}): {baseResult.email_fallidos.join(", ")}</p>}
                  <ClaimLinks links={baseResult.links_fallback} testid="claim-links-fallback-import" />
                </div>
              )}
              <ClaimLinks links={claimLinks} testid="claim-links-fallback-resend" />
              {pendientes.length > 0 && (
                <div className="mt-5">
                  <p className="text-xs font-semibold text-[var(--brand-primary)] mb-2">Cuentas pendientes de activación ({pendientes.length})</p>
                  <div className="space-y-1.5 max-h-56 overflow-auto" data-testid="pendientes-list">
                    {pendientes.map((u) => (
                      <div key={u.id} className="flex items-center justify-between gap-2 text-sm border-b border-slate-100 py-1.5" data-testid={`pendiente-${u.id}`}>
                        <span className="text-[var(--brand-primary)] truncate">{u.name} · <span className="text-[var(--brand-text-muted)]">{u.email}</span></span>
                        <button onClick={() => resendClaim(u.id)} className="btn-outline text-xs shrink-0 inline-flex items-center gap-1" data-testid={`resend-${u.id}`}><Send size={12} /> Reenviar</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
            <Card className="mb-6" data-testid="csv-import-card">
              <div className="flex items-center gap-2 mb-2"><FileSpreadsheet size={18} className="text-[var(--brand-accent)]" /><h3 className="font-heading font-bold text-[var(--brand-primary)]">Importación masiva (CSV)</h3></div>
              <p className="text-sm text-[var(--brand-text-muted)] mb-4">Cargá los niveles del informe Human AI para muchos egresados/as de una vez. Columnas: <b>email_egresado</b> + una por cada factor con su nivel (<b>Bajo, Medio, Medio alto, Alto, Muy alto</b>). Solo se actualizan los factores presentes; los niveles no reconocidos se informan y no se guardan.</p>
              <div className="flex flex-wrap gap-3">
                <button onClick={downloadTemplate} className="btn-outline text-sm inline-flex items-center gap-2" data-testid="csv-template-btn"><Download size={15} /> Descargar plantilla</button>
                <label className={`btn-accent text-sm inline-flex items-center gap-2 cursor-pointer ${importing ? "opacity-60 pointer-events-none" : ""}`} data-testid="csv-import-label">
                  <Upload size={15} /> {importing ? "Importando…" : "Subir CSV"}
                  <input type="file" accept=".csv" hidden onChange={importCsv} data-testid="csv-import-input" />
                </label>
              </div>
              {importResult && (
                <div className="mt-4 rounded-xl bg-[var(--brand-surface)] p-4 text-sm" data-testid="csv-import-result">
                  <p className="text-[var(--brand-primary)]"><b>{importResult.actualizados}</b> actualizados · <b>{importResult.procesados}</b> filas procesadas</p>
                  {importResult.no_encontrados?.length > 0 && (
                    <p className="text-[var(--status-descartada)] mt-1">No encontrados ({importResult.no_encontrados.length}): {importResult.no_encontrados.join(", ")}</p>
                  )}
                  {importResult.niveles_invalidos?.length > 0 && (
                    <p className="text-[var(--status-descartada)] mt-1" data-testid="csv-niveles-invalidos">Niveles no reconocidos ({importResult.niveles_invalidos.length}): {importResult.niveles_invalidos.map((x) => `${x.email} · ${x.factor}`).join(", ")}</p>
                  )}
                </div>
              )}
            </Card>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {egresados.map((e) => (
                <Card key={e.id} data-testid={`admin-egresado-${e.id}`}>
                  <h3 className="font-heading font-bold text-[var(--brand-primary)]">{e.name}</h3>
                  <p className="text-xs text-[var(--brand-text-muted)]">{e.marca} · {e.ubicacion}</p>
                  <p className="text-xs mt-2 text-[var(--brand-text-muted)]">{Object.values(e.informe_human_ai?.factores || {}).filter(Boolean).length}/15 factores del informe cargados</p>
                  <button onClick={() => openInforme(e)} className="btn-primary text-sm mt-3" data-testid={`cargar-fortalezas-${e.id}`}>Cargar / editar informe</button>
                  <button onClick={() => toggleDestacado(e)} data-testid={`destacado-toggle-${e.id}`}
                    className={`text-sm mt-2 w-full rounded-full border-2 px-3 py-1.5 transition-colors ${e.destacado ? "bg-[var(--brand-accent)] text-white border-[var(--brand-accent)]" : "border-[var(--brand-border)] text-[var(--brand-primary)] hover:border-[var(--brand-accent)]"}`}>
                    {e.destacado ? "★ Destacado (quitar)" : "☆ Marcar destacado"}
                  </button>
                </Card>
              ))}
            </div>
          </div>
        )}

        {tab === "egresados" && selEgr && (
          <InformeFactoresEditor egresado={selEgr} onBack={() => { setSelEgr(null); loadEgresados(); }} />
        )}

        {tab === "catalogos" && (
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <h3 className="font-heading font-bold text-[var(--brand-primary)] mb-3">Agregar al catálogo</h3>
              <div className="space-y-3">
                <select value={newCat.tipo} onChange={(e) => setNewCat({ ...newCat, tipo: e.target.value })} data-testid="cat-tipo" className="w-full rounded-xl border-2 border-[var(--brand-border)] px-4 py-2.5 text-sm">
                  <option value="skill_hard">Skill hard</option><option value="skill_soft">Skill soft</option><option value="formacion">Formación</option>
                </select>
                <Field label="Nombre" value={newCat.nombre} onChange={(e) => setNewCat({ ...newCat, nombre: e.target.value })} data-testid="cat-nombre" />
                <button onClick={addCat} className="btn-primary" data-testid="cat-add">Agregar</button>
              </div>
            </Card>
            {["skill_hard", "skill_soft", "formacion"].map((tipo) => (
              <Card key={tipo}>
                <h3 className="font-heading font-bold text-[var(--brand-primary)] mb-3 capitalize">{tipo.replace("_", " ")}</h3>
                <div className="flex flex-wrap gap-2">
                  {(catalogs[tipo] || []).map((n) => (
                    <span key={n} className="chip" data-testid={`cat-${tipo}-${n}`}>{n}<button onClick={() => delCat(tipo, n)} className="ml-1 text-red-400"><Trash2 size={11} /></button></span>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        )}

        {tab === "comunidad" && comunidad && (
          <div className="grid lg:grid-cols-2 gap-6">
            <Card data-testid="feed-create-card">
              <h3 className="font-heading font-bold text-[var(--brand-primary)] mb-3">Publicar en la comunidad</h3>
              <div className="space-y-3">
                <select value={newFeed.tipo} onChange={(e) => setNewFeed({ ...newFeed, tipo: e.target.value })} data-testid="feed-tipo"
                  className="w-full rounded-xl border-2 border-[var(--brand-border)] px-4 py-2.5 text-sm">
                  <option value="noticia">Noticia</option>
                  <option value="articulo">Artículo</option>
                  <option value="oportunidad">Oportunidad</option>
                </select>
                <Field label="Título" value={newFeed.titulo} onChange={(e) => setNewFeed({ ...newFeed, titulo: e.target.value })} data-testid="feed-titulo" />
                <div>
                  <label className="block text-sm font-semibold text-[var(--brand-primary)] mb-1.5">Detalle</label>
                  <textarea value={newFeed.detalle} onChange={(e) => setNewFeed({ ...newFeed, detalle: e.target.value })} data-testid="feed-detalle" rows={4}
                    className="w-full rounded-xl border-2 border-[var(--brand-border)] px-4 py-2.5 text-sm" placeholder="Contá la novedad, el artículo o la oportunidad…" />
                </div>
                <button onClick={crearFeed} className="btn-primary inline-flex items-center gap-2" data-testid="feed-publish-btn"><Plus size={16} /> Publicar</button>
              </div>
            </Card>
            <Card data-testid="feed-list-card">
              <h3 className="font-heading font-bold text-[var(--brand-primary)] mb-3">Publicaciones ({feed.length})</h3>
              <div className="space-y-2 max-h-[32rem] overflow-auto" data-testid="feed-list">
                {feed.map((f) => (
                  <div key={f.id} className="flex items-start justify-between gap-3 border-b border-slate-100 py-2.5" data-testid={`feed-item-${f.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="chip capitalize text-xs">{f.tipo}</span>
                        <span className="font-semibold text-[var(--brand-primary)] truncate">{f.titulo}</span>
                      </div>
                      {f.detalle && <p className="text-xs text-[var(--brand-text-muted)] mt-1 line-clamp-2">{f.detalle}</p>}
                      <p className="text-[11px] text-[var(--brand-text-muted)] mt-1">{f.autor} · {f.created_at ? new Date(f.created_at).toLocaleDateString("es-AR") : ""}</p>
                    </div>
                    <button onClick={() => delFeed(f.id)} className="text-red-400 shrink-0" data-testid={`feed-delete-${f.id}`} title="Eliminar"><Trash2 size={15} /></button>
                  </div>
                ))}
                {feed.length === 0 && <p className="text-sm text-[var(--brand-text-muted)] py-6">Todavía no hay publicaciones.</p>}
              </div>
            </Card>
          </div>
        )}

        {tab === "usuarios" && user?.rol === "super_admin" && <UsuariosInternos />}
        {tab === "auditoria" && (          <div className="card-brand overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--brand-surface)] text-left"><tr>{["Fecha", "Usuario", "Rol", "Acción", "Objetivo"].map((h) => <th key={h} className="px-4 py-3 font-heading text-[var(--brand-primary)]">{h}</th>)}</tr></thead>
              <tbody>
                {audit.map((a) => (
                  <tr key={a.id} className="border-t border-slate-100" data-testid={`audit-${a.id}`}>
                    <td className="px-4 py-2.5 text-[var(--brand-text-muted)]">{new Date(a.timestamp).toLocaleString("es-AR")}</td>
                    <td className="px-4 py-2.5">{a.user_email}</td>
                    <td className="px-4 py-2.5"><Chip>{a.rol}</Chip></td>
                    <td className="px-4 py-2.5 font-semibold text-[var(--brand-primary)]">{a.accion}</td>
                    <td className="px-4 py-2.5 text-[var(--brand-text-muted)]">{a.target_type}:{a.target_id}</td>
                  </tr>
                ))}
                {audit.length === 0 && <tr><td className="px-4 py-6 text-[var(--brand-text-muted)]" colSpan={5}>Sin registros de auditoría aún.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}
